/**
 * useSiteCoverageMatch — the ONE ranked DCT coverage match per site page.
 *
 * Runs `matchUploadedDocsToChecklistRanked` for every report type in the
 * coverage scope — saved types plus the canonical planned-analysis union,
 * the scope the Site Evidence CoverageMatrix walks — and serves three consumers
 * from that single run: the CoverageMatrix, the Author-evidence lineage prefill,
 * and Path to GO's `coverageMatchByType` (Home band, Decision tab, Evidence
 * ledger, Findings). Without it Path to GO only saw the sync tag-pattern
 * fallback, so an upload found by DCT alone read as an "Add evidence" ask.
 *
 * Results are cached per session (`siteCoverageMatchCache`): a revisit paints
 * from the cache in the same render and scores nothing. A run in which some
 * DM2 scores failed transiently (a timeout or API error, a rate limit
 * included — `RankedChecklistMatch.transientFailures`) is shown by this mount
 * like any other, but is not final: once its re-run is due (60 s, doubling)
 * the next mount misses the cache and re-scores, showing the partial rows as
 * the interim meanwhile. There is no timer — a mount re-scores at most once
 * for the same inputs, so a failing pair cannot loop.
 *
 * Leaving the page (or new inputs) aborts the run: pairs still waiting for a
 * DM2 scoring slot are dropped, so an old run does not hold the tab's Gemini
 * budget; active SDK calls/retries are cancelled and abandoned verdicts are not published.
 *
 * `enabled` holds the FIRST run for a site until the inputs have settled. The
 * page passes "the composite has resolved" — that lands once reports and the
 * SED have loaded, and it is what fixes the decision stage, so starting
 * earlier would score a scope that is about to change. After that the hook
 * latches the settled stage, so a transient reload does not restart the run.
 */
import React from 'react';
import type { SiteDocument, SiteRagReport } from '../../types/siteRag';
import {
  getCoverageScopeReportTypes,
  reportTypeStage,
  type DecisionStage,
  type ReportTypeId,
} from '../../config/reportTypes';
import type { RankedChecklistMatch, RankedDocMatch } from '../../config/documentChecklists';
import {
  buildSiteCoverageMatchKey,
  getCachedSiteCoverageMatch,
  getLatestCachedSiteCoverageMatch,
  scoredCoverageDocuments,
  setCachedSiteCoverageMatch,
} from '../../lib/siteCoverageMatchCache';

type MatchByType = Map<ReportTypeId, RankedChecklistMatch>;

/**
 * The report types a site's saved reports cover, in first-seen order — the
 * site-owned scope `getCoverageScopeReportTypes` starts from. Existing callers
 * without a planned set retain this exact saved-type policy.
 */
export function siteReportTypeScope(reports: readonly SiteRagReport[]): ReportTypeId[] {
  const seen = new Set<ReportTypeId>();
  for (const r of reports) seen.add((r.reportType ?? 'feasibility') as ReportTypeId);
  return [...seen];
}

/** Existing saved types retain their scope. New planned types join this same
 * run only at their decision stage. A reordered/duplicated plan is one value,
 * so it cannot spend another set of requests or produce another cache key. */
export function siteCoverageReportTypeScope(
  reports: readonly SiteRagReport[], plannedReportTypes: readonly ReportTypeId[] = [],
  decisionStage?: DecisionStage,
): ReportTypeId[] {
  const saved = siteReportTypeScope(reports);
  const planned = [...new Set(plannedReportTypes)].sort()
    .filter(type => decisionStage !== 1 || reportTypeStage(type) === 1);
  return getCoverageScopeReportTypes([...new Set([...saved, ...planned])], { decisionStage });
}

/** The last COMPLETED run, whatever inputs it was computed on. */
export interface CoverageMatchSnapshot {
  siteId: string;
  matchByType: MatchByType;
}

/** The inputs of the run now pending, as the interim policy needs them. */
export interface PendingCoverageMatchInputs {
  siteId: string;
  /** The coverage scope the pending run scores. */
  reportTypes: readonly ReportTypeId[];
  /** Ids of the documents the pending run scores. */
  liveDocIds: ReadonlySet<string>;
  /** `${docId}::${requirementId}` pairs the user has disconnected. */
  disconnected: ReadonlySet<string>;
}

/**
 * What the page shows while a run for NEW inputs is in flight and the cache
 * has nothing for them: a document was uploaded or deleted, a link was
 * disconnected, or the scope or stage changed.
 *
 * The last result, minus everything the new inputs already rule out — a
 * removed document, a user disconnect, a report type that left the scope.
 * Whatever this returns reaches Path to GO at once, so dropping everything
 * would flip every "Verify evidence" row back to "Add evidence" (with the
 * draft-email ask) for the whole run, right after an upload; keeping rows the
 * new inputs exclude would leave Tier-3 status on a document the user just
 * deleted or unlinked, because Tier 3 reads `matched` without re-checking
 * either. Another site's result is never shown.
 *
 * Only `matched` is filtered: it is the field every consumer reads (the
 * CoverageMatrix, requirement status, Path to GO); the run that follows
 * recomputes the rest, including Primary re-assignment.
 */
export function interimCoverageMatch(
  last: CoverageMatchSnapshot | null,
  next: PendingCoverageMatchInputs,
): MatchByType {
  if (!last || last.siteId !== next.siteId) return new Map();
  return filterMatchedRows(last.matchByType, next.reportTypes, (docId, requirementId) =>
    next.liveDocIds.has(docId) && !next.disconnected.has(`${docId}::${requirementId}`));
}

/**
 * `matchByType` restricted to rows for `documents` — the map Path to GO
 * receives. Path to GO names matched uploads from `site.documents` only (its
 * step copy reads their file names there), while Tier 3 does not check
 * document ids at all, so a match on a legacy report-attached document would
 * lift a row's severity while its copy still asked for the upload. The Site
 * Evidence matrix keeps the full set it has always matched.
 */
export function coverageMatchForDocuments(
  matchByType: ReadonlyMap<ReportTypeId, RankedChecklistMatch>,
  documents: readonly SiteDocument[],
): MatchByType {
  const ids = new Set(scoredCoverageDocuments(documents).map(d => d.id));
  return filterMatchedRows(matchByType, matchByType.keys(), docId => ids.has(docId));
}

/**
 * The latest settled result for `siteId`: this mount's own, else the session
 * cache's. A remount (after opening a report or another site mid-run) keeps
 * no result of its own, and a cancelled run writes nothing, so without the
 * cache's the interim would be empty for the whole re-run.
 */
function latestSettled(last: CoverageMatchSnapshot | null, siteId: string): CoverageMatchSnapshot | null {
  if (last?.siteId === siteId) return last;
  const cached = getLatestCachedSiteCoverageMatch(siteId);
  return cached ? { siteId, matchByType: cached } : null;
}

/** `matchByType` for `reportTypes`, keeping only the `matched` rows `keep` accepts. */
function filterMatchedRows(
  matchByType: ReadonlyMap<ReportTypeId, RankedChecklistMatch>,
  reportTypes: Iterable<ReportTypeId>,
  keep: (docId: string, requirementId: string) => boolean,
): MatchByType {
  const out: MatchByType = new Map();
  for (const type of reportTypes) {
    const match = matchByType.get(type);
    if (!match) continue;
    const matched = new Map<string, RankedDocMatch[]>();
    for (const [requirementId, rows] of match.matched) {
      matched.set(requirementId, rows.filter(row => keep(row.docId, requirementId)));
    }
    out.set(type, { ...match, matched });
  }
  return out;
}

export interface UseSiteCoverageMatchArgs {
  siteId: string;
  /** The page's merged document list (site documents ∪ report documents). */
  documents: SiteDocument[];
  /** The site's reports; their types are the site-owned coverage scope. */
  reports: readonly SiteRagReport[];
  /** Canonical Path-to-GO plan; unioned into this run, never a second matcher. */
  plannedReportTypes?: readonly ReportTypeId[];
  disconnectedLinks?: ReadonlyArray<{ docId: string; targetId: string }>;
  /** Stage 1 plans only Stage 1 types when the site has no reports yet. */
  decisionStage?: DecisionStage;
  /**
   * True once the inputs have settled. No run starts before the first true for
   * this site; a later false (a transient reload) keeps the last settled stage
   * and does not cancel a run in flight. A cached entry is served either way.
   */
  enabled: boolean;
}

export interface SiteCoverageMatchState {
  /** Exact resolved scope shared with the matrix/author-prefill consumers. */
  reportTypes: readonly ReportTypeId[];
  /** Ranked match per report type in scope — stable identity between runs. */
  matchByType: MatchByType;
  /** True while `matchByType` is not yet the result for the current inputs. */
  loading: boolean;
}

export function useSiteCoverageMatch(args: UseSiteCoverageMatchArgs): SiteCoverageMatchState {
  const { siteId, documents, reports, plannedReportTypes, disconnectedLinks, decisionStage, enabled } = args;

  // Latch the settled inputs per site. Once this site has settled, a transient
  // reload (a SED version bump nulls the page's composite for a moment) must
  // neither cancel the run in flight nor flip a report-less Stage 1 site's
  // scope to the full planning set and back. A real stage change re-latches.
  const [settled, setSettled] = React.useState<{ siteId: string; decisionStage?: DecisionStage } | null>(null);
  if (enabled && (settled === null || settled.siteId !== siteId || settled.decisionStage !== decisionStage)) {
    setSettled({ siteId, decisionStage });
  }
  const latched = settled !== null && settled.siteId === siteId ? settled : null;
  const runEnabled = enabled || latched !== null;
  const scopeStage = enabled ? decisionStage : latched?.decisionStage;

  // The scope by VALUE: a new reports array with the same types, or a runtime
  // status-cache refresh that leaves the fallback set unchanged, must not look
  // like new inputs.
  const reportTypesKey = siteCoverageReportTypeScope(reports, plannedReportTypes, scopeStage).join(',');
  const reportTypes = React.useMemo(
    () => (reportTypesKey ? (reportTypesKey.split(',') as ReportTypeId[]) : []),
    [reportTypesKey],
  );
  const key = React.useMemo(
    () => buildSiteCoverageMatchKey({ siteId, documents, disconnectedLinks, reportTypes }),
    [siteId, documents, disconnectedLinks, reportTypes],
  );
  // Read whatever the gate says: an entry is valid for exactly these inputs.
  const cached = React.useMemo(() => getCachedSiteCoverageMatch(siteId, key), [siteId, key]);

  // The pending run's inputs by VALUE, so the interim map — and with it Path
  // to GO — recomputes only when what the run will score actually changes.
  const liveDocKey = scoredCoverageDocuments(documents).map(d => d.id).sort().join('\n');
  const disconnectKey = (disconnectedLinks ?? []).map(l => `${l.docId}::${l.targetId}`).sort().join('\n');
  const pending = React.useMemo<PendingCoverageMatchInputs>(() => ({
    siteId,
    reportTypes,
    liveDocIds: new Set(liveDocKey ? liveDocKey.split('\n') : []),
    disconnected: new Set(disconnectKey ? disconnectKey.split('\n') : []),
  }), [siteId, reportTypes, liveDocKey, disconnectKey]);

  const [last, setLast] = React.useState<(CoverageMatchSnapshot & { key: string }) | null>(null);

  React.useEffect(() => {
    if (cached) {
      // A hit is this site's latest settled result too: the interim policy
      // keeps it while a later run (after an upload, say) is in flight.
      setLast(prev => (prev?.key === key ? prev : { siteId, key, matchByType: cached }));
      return;
    }
    if (!runEnabled) return;
    let cancelled = false;
    const controller = new AbortController();
    (async () => {
      try {
        const { matchUploadedDocsToChecklistRanked } = await import('../../config/documentChecklists');
        const next: MatchByType = new Map();
        for (const type of reportTypes) {
          if (cancelled) return;
          next.set(type, await matchUploadedDocsToChecklistRanked(documents, type, {
            siteId,
            ...(disconnectedLinks ? { disconnectedLinks } : {}),
            signal: controller.signal,
          }));
        }
        if (cancelled) return;
        // A run with transient failures is stored too — it is this site's
        // latest result — but the cache schedules its re-run.
        setCachedSiteCoverageMatch(siteId, key, next);
        setLast({ siteId, key, matchByType: next });
      } catch (e) {
        // The matcher should never throw (DM2 turns failures into 0-scores).
        // A wiring bug must not blank the surfaces or leave `loading` stuck:
        // settle on what the interim showed — the last result minus what these
        // inputs rule out — uncached, so a remount retries.
        if (cancelled) return;
        console.debug('[useSiteCoverageMatch] ranked matcher failed:', (e as Error)?.message);
        setLast(prev => ({ siteId, key, matchByType: interimCoverageMatch(latestSettled(prev, siteId), pending) }));
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // `key` encodes every matcher input by value (site, scope, scored documents,
    // disconnects), so identity-only changes of those arrays do not restart a run;
    // `pending` is derived from the same inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runEnabled, cached, siteId, key]);

  const interim = React.useMemo(
    () => interimCoverageMatch(latestSettled(last, siteId), pending),
    [last, siteId, pending],
  );

  if (cached) return { reportTypes, matchByType: cached, loading: false };
  if (last?.key === key) return { reportTypes, matchByType: last.matchByType, loading: false };
  return { reportTypes, matchByType: interim, loading: true };
}
